import { ActivatedRouteSnapshot, CanActivate, GuardResult, MaybeAsync, RouterModule, RouterStateSnapshot, Routes, UrlTree } from '@angular/router';
import { FailedComponent } from './pages/failed/failed.component';
import { CallbackComponent } from './pages/callback/callback.component';
import { AuthGuard } from '@auth0/auth0-angular';
import { Injectable } from '@angular/core';
import { ClientComponent } from './pages/client/client.component';
import { ClientDetailComponent } from './pages/client-detail-view/client-detail.component'
import { VersionManagementComponent } from './pages/version-management/version-management.component';
import { MapViewComponent } from './pages/schedules/map-view/map-view.component';
import { GlobalUsersComponent } from './pages/global-users/global-users.component';
import { DashboardAccessComponent } from './pages/dashboard-access/dashboard-access.component';
import { GroupRecordAccessSyncComponent } from './pages/group-record-access-sync/group-record-access-sync.component';
import { SchedulesHeaderComponent } from './pages/schedules/schedules-header/schedules-header.component';
import { RouterService } from './utility/router-service';
import { LoginService } from '../services/login-service';
import { UserRegistrationComponent } from './pages/user-registration/user-registration.component';
import { PendingUsersComponent } from './pages/pending-users/pending-users.component';
import { AddJobComponent } from './pages/schedules/schedules-header/add-job.component';
import { JobGridComponent } from './pages/schedules/schedules-header/job-grid.component';
import { UserSettingsComponent } from './pages/user-settings/user-settings.component';
import { UserManagementComponent } from './pages/user-management/user-management.component';
import { WaterSharingWelcomeComponent } from './pages/water-sharing/ws-welcome/ws-welcome.component';
import { WaterSharingHomeComponent } from './pages/water-sharing/ws-home/ws-home.component';
import { DevSupportComponent } from './pages/dev-support/dev-support.component';
import { UserManualsComponent } from './pages/user-manuals/user-manuals.component';
import { DashboardsComponent } from './pages/dashboards/dashboards.component';
import { WcHomeComponent } from './pages/wc-home/wc-home.component';
import { PolicyExplorerMainComponent } from './pages/policy-explorer/policy-explorer-main/policy-explorer-main.component';
import { ProdDataComponent } from './pages/prod-data/prod-data.component';
import { ProdDataDetailComponent } from './pages/prod-data/prod-data-detail/prod-data-detail.component';
import { AttachmentHierarchyEditorComponent } from './pages/attachment-hierarchy-editor/attachment-hierarchy-editor.component';
import { ModelsEditorComponent } from './pages/models-editor/models-editor.component';
import { TaskEventHistoryComponent } from './pages/task-event-history/task-event-history.component';

@Injectable({
  providedIn: 'root'
})
export class CheckHomeGuard implements CanActivate {
  constructor(private loginService: LoginService, private router: RouterService, private authInit: LoginService) {

  }
  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<GuardResult> {
    if (!this.loginService.userOptional)
      await this.authInit.ensureUserLoaded();
    if (this.loginService.userOptional?.defaultApplication === 'FracX')
      return this.router.router.parseUrl("/schedules");
    else
      return this.router.router.parseUrl("/home");
  }

}
@Injectable({ providedIn: 'root' })
export class AnyAdminGuard implements CanActivate {
  constructor(private loginService: LoginService, private router: RouterService) {}

  async canActivate(): Promise<GuardResult> {
    const user = this.loginService.userOptional;
    if (user?.isGlobalAdmin || user?.isDeveloper || user?.isCompanyAdmin)
      return true;
    return this.router.router.parseUrl('/home');
  }
}

@Injectable({ providedIn: 'root' })
export class GlobalAdminGuard implements CanActivate {
  constructor(private loginService: LoginService, private router: RouterService) {}

  async canActivate(): Promise<GuardResult> {
    const user = this.loginService.userOptional;
    if (user?.isGlobalAdmin)
      return true;
    return this.router.router.parseUrl('/home');
  }
}

// Distinct from AnyAdminGuard/GlobalAdminGuard: routes gated by this one write directly to
// the Enverus Accounts Service (production identity data), so they require the developer
// role specifically rather than any admin role.
@Injectable({ providedIn: 'root' })
export class DeveloperGuard implements CanActivate {
  constructor(private loginService: LoginService, private router: RouterService) {}

  async canActivate(): Promise<GuardResult> {
    const user = this.loginService.userOptional;
    if (user?.isDeveloper)
      return true;
    return this.router.router.parseUrl('/home');
  }
}

@Injectable({ providedIn: 'root' })
export class FracXGuard implements CanActivate {
  constructor(private loginService: LoginService, private router: RouterService) {}

  async canActivate(): Promise<GuardResult> {
    const user = this.loginService.userOptional;
    if (user?.enableFracX || user?.isGlobalAdmin)
      return true;
    return this.router.router.parseUrl('/home');
  }
}

@Injectable({
  providedIn: 'root'
})
export class CheckUserGuard implements CanActivate {
  constructor(private loginService: LoginService, private router: RouterService, private authInit: LoginService) {

  }
  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<GuardResult> {
    if (!this.loginService.userOptional)
      await this.authInit.ensureUserLoaded();
      
    if (this.loginService.userOptional?.isPending)
      return this.router.router.parseUrl("/user-registration");
    return true;
  }

}

@Injectable({ providedIn: 'root' })
export class UserHydrationGuard {
  constructor(
    private authInit: LoginService,
    private router: RouterService
  ) { }

  async canActivate(): Promise<GuardResult> {
    // A rejection here (network error, or the 401 WellChron.Server now returns
    // for an authenticated Enverus account with no WellChron access role) must
    // not propagate as an unhandled promise rejection: that aborts navigation
    // silently, leaving a blank shell with no UI to even log out from, since
    // the header/logout control is itself gated on the user context this call
    // was supposed to populate.
    try {
      await this.authInit.ensureUserLoaded();
    } catch {
      return this.router.router.parseUrl('/login-failed');
    }

    if (!this.authInit.isLoaded) {
      return this.router.router.parseUrl('/login-failed');
    }

    return true;
  }
}

export const routes: Routes = [
  {
    path: 'client',
    component: ClientComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard],
  },
  {
    path: 'client/:id',
    component: ClientDetailComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard],
  },

  {
    path: 'watersharing/welcome',
    component: WaterSharingWelcomeComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard],
  },

  {
    path: 'watersharing',
    component: WaterSharingHomeComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard],
  },
  {
    path: 'global-users',
    component: GlobalUsersComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard]
  },
  {
    path: 'dashboard-access',
    component: DashboardAccessComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard, GlobalAdminGuard]
  },
  {
    path: 'group-record-access-sync',
    component: GroupRecordAccessSyncComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard, GlobalAdminGuard]
  },
  {
    path: 'data-explorer/:clientId/:schemaName/:tableName/:recordId',
    component: PolicyExplorerMainComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard]
  },
  {
    path: 'pending-users',
    component: PendingUsersComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard],
  },
  {
    path: 'dashboards',
    component: DashboardsComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard],
  },
  {
    path: 'schedules',
    component: SchedulesHeaderComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard, FracXGuard],
  },
  {
    path: 'api/schedules',
    component: MapViewComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard, FracXGuard],
  },
  {
    path: 'api/schedules/all',
    component: MapViewComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard, FracXGuard],
  },
  {
    path: 'version-management',
    component: VersionManagementComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard],
  },
  {
    path: 'user-settings',
    component: UserSettingsComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard]
  },
  {
    path: 'user-management',
    component: UserManagementComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard]
  },
  {
    path: 'user-registration',
    component: UserRegistrationComponent,
    canActivate: [AuthGuard, UserHydrationGuard]
  },
  {
    // Auth0 redirect target. No guards -- it has to be reachable while still
    // unauthenticated, and AuthGuard here would bounce the user straight back
    // into another login redirect.
    path: 'callback',
    component: CallbackComponent,
  },
  {
    path: 'login-failed',
    component: FailedComponent,
  },
  {
    path: '',
    component: WcHomeComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard, CheckHomeGuard],
  },
  {
    path: 'home',
    component: WcHomeComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard],
  },

  {
    path: 'add-job',
    component: AddJobComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard, FracXGuard],
  },

  {
    path: 'job-grid',
    component: JobGridComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard, FracXGuard],
  },
  {
    path: 'contact-us',
    component: DevSupportComponent,
  },
  {
    path: 'user-manuals',
    component: UserManualsComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard],
  },
  {
    path: 'prod-data',
    component: ProdDataComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard, GlobalAdminGuard],
  },
  {
    path: 'prod-data/:clientId',
    component: ProdDataDetailComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard, GlobalAdminGuard],
  },
  {
    path: 'attachment-hierarchies',
    component: AttachmentHierarchyEditorComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard, AnyAdminGuard],
  },
  {
    path: 'models',
    component: ModelsEditorComponent,
    canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard, AnyAdminGuard],
  },
  {
    path: 'task-event-history',
    component: TaskEventHistoryComponent,
      canActivate: [AuthGuard, UserHydrationGuard, CheckUserGuard, AnyAdminGuard],
  }
];
